/**
 * Typeahead popover for response path autocomplete.
 *
 * Shows filtered dot-notation path suggestions with value previews,
 * anchored below the input. Keyboard navigable and click-to-select.
 *
 * @module FlowDesigner/api/ResponsePathTypeahead
 */

import React, { useEffect, useRef } from 'react';
import type { PathSuggestion } from './hooks/useResponsePathTypeahead';

interface ResponsePathTypeaheadProps {
  suggestions: PathSuggestion[];
  highlightedIndex: number;
  onSelect: (suggestion: PathSuggestion) => void;
}

export const ResponsePathTypeahead: React.FC<ResponsePathTypeaheadProps> = ({
  suggestions,
  highlightedIndex,
  onSelect,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const highlighted = listRef.current?.children[highlightedIndex] as HTMLElement | undefined;
    highlighted?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  if (suggestions.length === 0) return null;

  return (
    <div
      ref={listRef}
      className="absolute left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-popover shadow-md"
      role="listbox"
    >
      {suggestions.map((suggestion, index) => (
        <div
          key={suggestion.path}
          role="option"
          aria-selected={index === highlightedIndex}
          className={`cursor-pointer px-3 py-1.5 text-sm ${
            index === highlightedIndex
              ? 'bg-accent text-accent-foreground'
              : 'text-foreground hover:bg-accent/50'
          }`}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(suggestion);
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono font-medium text-primary truncate">
              {suggestion.path}
            </span>
            <span className="text-xs text-muted-foreground font-mono truncate max-w-[200px]">
              {suggestion.preview}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};
